/* eslint-disable prettier/prettier */
import {
  Grid,
  TextField,
  Box,
  MenuItem,
  Button,
  FormControlLabel,
  Checkbox,
  Typography
} from "@material-ui/core";
import { useFormik } from "formik";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useEffect, useMemo, useState, useCallback } from "react";
import * as Yup from "yup";
import Alert from "@material-ui/lab/Alert";
import { useAppContext } from "../../api/AppContext";
import { isCyberTipsSection, getCyberTipsEntityLabel } from "./cyberTipsSections";
import {
  LANGUAGE_LABELS,
  ensurePromptMultilingualObject,
  formatLanguageList,
  getCompletionState,
  getEditableLanguagesForPromptSection,
  getIncompleteLanguages,
  getMandatoryLanguagesForPromptSection,
  getPreferredLanguageFromUser,
  getValidLanguagesFromUser,
  isPromptLanguageCompleteForSave,
  requiresAllMandatoryLanguages,
} from "../shared/languageConfig";
import { LanguageChips, PinnedLanguageField } from "../shared/LanguageSelector";
import { usePreferredLanguagePersistence } from "../shared/usePreferredLanguagePersistence";

const Prompt = ({
  basePath,
  formik,
  tags,
  tools,
  isUserPromptEdit = false,
  canSaveUserPrompt = false,
  onCreatePromptFromUserPrompt,
  preferredLanguage,
  setPreferredLanguage,
  validLanguages,
}) => {
  const supportsTools = !isCyberTipsSection(basePath);
  const getFieldLabel = useCallback(
    ({ targetKeyword } = {}) => {
      const tab = targetKeyword;
      switch (tab) {
        case "prompt":
          switch (basePath) {
            case "allitude":
              return "Seed";
            default:
              if (isCyberTipsSection(basePath)) {
                return getCyberTipsEntityLabel();
              }
              return "Prompt";
          }
        case "tool":
          switch (basePath) {
            case "allitude":
              return "Topic";
            default:
              return "Tool";
          }
        case "tag":
          return "Tag";
        default:
          return "";
      }
    },
    [basePath]
  );

  const handleInputChange = (name, value, lang) => {
    if (lang) {
      formik.setFieldValue(name, {
        ...formik.values[name],
        [lang]: value
      });
    } else {
      formik.setFieldValue(name, value);
    }
  };

  const secondaryLanguages = validLanguages.filter(
    (language) => language !== preferredLanguage
  );
  const [selectedPreferredLanguage, setSelectedPreferredLanguage] = useState(
    preferredLanguage
  );
  const [activeSecondaryLanguage, setActiveSecondaryLanguage] = useState(
    secondaryLanguages[0] || null
  );
  const persistPreferredLanguage = usePreferredLanguagePersistence();

  const handleConfirmPreferredLanguage = async () => {
    setPreferredLanguage(selectedPreferredLanguage);
    await persistPreferredLanguage(selectedPreferredLanguage);
  };

  useEffect(() => {
    setSelectedPreferredLanguage(preferredLanguage);
  }, [preferredLanguage]);

  useEffect(() => {
    if (!secondaryLanguages.length) {
      setActiveSecondaryLanguage(null);
      return;
    }

    if (!secondaryLanguages.includes(activeSecondaryLanguage)) {
      setActiveSecondaryLanguage(secondaryLanguages[0]);
    }
  }, [activeSecondaryLanguage, secondaryLanguages]);

  const incompleteMandatoryLanguages = useMemo(() => {
    if (!requiresAllMandatoryLanguages(basePath)) {
      return [];
    }

    return getIncompleteLanguages(
      getMandatoryLanguagesForPromptSection(basePath),
      (language) => isPromptLanguageCompleteForSave(formik.values, language)
    );
  }, [basePath, formik.values]);

  const missingMandatoryLanguagesMessage = useMemo(() => {
    if (formik.submitCount === 0 || !incompleteMandatoryLanguages.length) {
      return null;
    }

    return `Saving requires these languages to be fully filled: ${formatLanguageList(
      incompleteMandatoryLanguages
    )}. Switch language chips to complete the missing translations.`;
  }, [formik.submitCount, incompleteMandatoryLanguages]);

  const languageCompletionByCode = useMemo(() => {
    return validLanguages.reduce((acc, language) => {
      const completed = ["title", "description", "prompttext"].reduce(
        (total, field) => {
          return total + (formik.values[field]?.[language]?.trim() ? 1 : 0);
        },
        0
      );

      acc[language] = getCompletionState({
        completedLanguage: completed,
        isActive: language === activeSecondaryLanguage,
        totalRequiredFields: 3,
      });
      return acc;
    }, {});
  }, [activeSecondaryLanguage, formik.values, validLanguages]);

  const renderLanguageColumn = (language) => {
    if (!language) {
      return null;
    }

    const languageLabel = LANGUAGE_LABELS[language] || language.toUpperCase();
    const entityLabel = getFieldLabel({ targetKeyword: "prompt" });
    const hasAtLeastOneCompleteLanguage = validLanguages.some((lang) =>
      isPromptLanguageCompleteForSave(formik.values, lang)
    );
    const useAtLeastOneRule = !requiresAllMandatoryLanguages(basePath);
    const showAtLeastOneFieldHints =
      useAtLeastOneRule &&
      formik.submitCount > 0 &&
      !hasAtLeastOneCompleteLanguage &&
      language === preferredLanguage;

    const fieldMessages = {
      description: "Description is mandatory",
      prompttext: `${entityLabel} is mandatory`,
      title: "Title is mandatory",
    };

    const shouldShowFieldError = (fieldName) => {
      const yupError =
        (formik.touched[fieldName]?.[language] || formik.submitCount > 0) &&
        Boolean(formik.errors[fieldName]?.[language]);
      if (yupError) {
        return true;
      }

      if (!showAtLeastOneFieldHints) {
        return false;
      }

      return !formik.values[fieldName]?.[language]?.trim();
    };

    const getFieldError = (fieldName) => {
      if (
        (formik.touched[fieldName]?.[language] || formik.submitCount > 0) &&
        formik.errors[fieldName]?.[language]
      ) {
        return formik.errors[fieldName][language];
      }

      if (
        showAtLeastOneFieldHints &&
        !formik.values[fieldName]?.[language]?.trim()
      ) {
        return fieldMessages[fieldName];
      }

      return undefined;
    };

    return (
      <>
        <Grid item xs={12}>
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={isUserPromptEdit}
            error={shouldShowFieldError("title")}
            helperText={getFieldError("title")}
            label={`Title (${languageLabel})`}
            margin="none"
            value={formik.values.title?.[language] || ""}
            variant="outlined"
            onBlur={() => formik.setFieldTouched(`title.${language}`, true)}
            onChange={(event) =>
              handleInputChange("title", event.target.value, language)
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={isUserPromptEdit}
            error={shouldShowFieldError("description")}
            helperText={getFieldError("description")}
            label={`Description (${languageLabel})`}
            margin="none"
            value={formik.values.description?.[language] || ""}
            variant="outlined"
            onBlur={() =>
              formik.setFieldTouched(`description.${language}`, true)
            }
            onChange={(event) =>
              handleInputChange("description", event.target.value, language)
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            InputLabelProps={{ shrink: true }}
            disabled={isUserPromptEdit}
            error={shouldShowFieldError("prompttext")}
            helperText={getFieldError("prompttext")}
            label={`${entityLabel} (${languageLabel})`}
            margin="none"
            rows={4}
            value={formik.values.prompttext?.[language] || ""}
            variant="outlined"
            onBlur={() =>
              formik.setFieldTouched(`prompttext.${language}`, true)
            }
            onChange={(event) =>
              handleInputChange("prompttext", event.target.value, language)
            }
          />
        </Grid>
      </>
    );
  };

  return (
    <Box
      boxSizing="border-box"
      component="form"
      display="flex"
      flexDirection="column"
      p={4}
      width="100%"
    >
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item md={secondaryLanguages.length ? 6 : 12} xs={12}>
            <Box mb={2} style={{ minHeight: 40 }}>
              <PinnedLanguageField
                requireSetConfirmation
                showInlineLabel
                disabled={isUserPromptEdit}
                inlineLabel="Select default language"
                validLanguages={validLanguages}
                value={selectedPreferredLanguage}
                onChange={setSelectedPreferredLanguage}
                onConfirm={handleConfirmPreferredLanguage}
              />
            </Box>
            <Grid container spacing={2}>
              {renderLanguageColumn(preferredLanguage)}
            </Grid>
          </Grid>
          {secondaryLanguages.length > 0 && (
            <Grid item md={6} xs={12}>
              <Box mb={2} style={{ minHeight: 40 }}>
                <LanguageChips
                  languageCompletionByCode={languageCompletionByCode}
                  languages={secondaryLanguages}
                  onLanguageClick={setActiveSecondaryLanguage}
                />
              </Box>
              <Grid container spacing={2}>
                {renderLanguageColumn(activeSecondaryLanguage)}
              </Grid>
            </Grid>
          )}

          {supportsTools && (
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                select
                InputLabelProps={{ shrink: true }}
                error={formik.touched.tool_id && Boolean(formik.errors.tool_id)}
                helperText={formik.touched.tool_id && formik.errors.tool_id}
                label={`${getFieldLabel({ targetKeyword: "tool" })}`}
                margin="none"
                value={formik.values.tool_id || ""}
                variant="outlined"
                onChange={(event) =>
                  handleInputChange("tool_id", event.target.value)
                }
              >
                {tools.map((tool) => (
                  <MenuItem key={tool.id} value={tool.id}>
                    {tool.toolname}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              select
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                multiple: true,
                onChange: (event) =>
                  handleInputChange("tagids", event.target.value),
                value: formik.values.tagids || []
              }}
              error={formik.touched.tagids && Boolean(formik.errors.tagids)}
              helperText={formik.touched.tagids && formik.errors.tagids}
              label="Tags"
              margin="none"
              variant="outlined"
            >
              {tags.map((tag) => (
                <MenuItem key={tag.id} value={tag.id}>
                  {tag.tagname?.en}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {!isCyberTipsSection(basePath) && !isUserPromptEdit && (
            <Grid container item justifyContent="center" mt={2} xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.isfromtypeform || false}
                    color="primary"
                    disabled={isUserPromptEdit}
                    name="isfromtypeform"
                    onChange={(event) =>
                      handleInputChange("isfromtypeform", event.target.checked)
                    }
                  />
                }
                label="isFromTypeform"
              />
            </Grid>
          )}

          {isUserPromptEdit && (
            <>
              <Grid item style={{ textAlign: "center" }} xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.processed || false}
                      color="primary"
                      name="processed"
                      onChange={(event) =>
                        handleInputChange("processed", event.target.checked)
                      }
                    />
                  }
                  label="Processed"
                />
              </Grid>
              <Grid item md={6} style={{ margin: "0 auto" }} xs={12}>
                <TextField
                  fullWidth
                  multiline
                  InputLabelProps={{ shrink: true }}
                  label="Note"
                  margin="none"
                  rows={4}
                  value={formik.values.note || ""}
                  variant="outlined"
                  onChange={(event) =>
                    handleInputChange("note", event.target.value)
                  }
                />
              </Grid>
            </>
          )}
        </Grid>

        {isUserPromptEdit ? (
          <Box display="flex" justifyContent="center" mt={2}>
            <Box mr={1}>
              <Button
                color="primary"
                variant="contained"
                onClick={onCreatePromptFromUserPrompt}
              >
                Create this prompt
              </Button>
            </Box>
            <Button
              color="primary"
              disabled={!canSaveUserPrompt}
              type="submit"
              variant="contained"
              onClick={formik.handleSubmit}
            >
              Save
            </Button>
          </Box>
        ) : (
          <Box alignItems="center" display="flex" flexDirection="column" mt={2}>
            {missingMandatoryLanguagesMessage && (
              <Box maxWidth={640} mb={2} width="100%">
                <Alert severity="warning">
                  {missingMandatoryLanguagesMessage}
                </Alert>
              </Box>
            )}
            <Button
              color="primary"
              type="submit"
              variant="contained"
              onClick={formik.handleSubmit}
            >
              Submit
            </Button>
          </Box>
        )}
      </Grid>
    </Box>
  );
};

const PromptContainer = () => {
  const { fetchTools, fetchTags, updatePrompt, tags, tools, user } =
    useAppContext();
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [configurationEntity, setConfigurationEntity] = useState(null);
  const { rowData } = location.state || {};
  const basePath = useMemo(
    () => location.pathname.split("/")[1],
    [location.pathname]
  );
  const [isLoading, setIsLoading] = useState(true);
  const validLanguages = useMemo(() => getValidLanguagesFromUser(user), [user]);
  const editableLanguages = useMemo(
    () => getEditableLanguagesForPromptSection(basePath, validLanguages),
    [basePath, validLanguages]
  );
  const [preferredLanguage, setPreferredLanguage] = useState(
    getPreferredLanguageFromUser(user, editableLanguages)
  );
  const currentTab = useMemo(
    () => location.pathname.split("/")[2],
    [location.pathname]
  );
  const isUserPromptEdit = currentTab === "user-prompts";

  useEffect(() => {
    setPreferredLanguage(getPreferredLanguageFromUser(user, editableLanguages));
  }, [user, editableLanguages]);

  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      if (isCyberTipsSection(basePath)) {
        await fetchTags(basePath);
      } else {
        await Promise.all([fetchTools(basePath), fetchTags(basePath)]);
      }
      setIsLoading(false);
    };
    initializeData();
  }, [fetchTags, fetchTools, basePath]);

  const getValidationSchema = (basePath, editableLanguages) => {
    const entityLabel = isCyberTipsSection(basePath) ? "Tip" : "Prompt";
    const mandatoryLanguages = getMandatoryLanguagesForPromptSection(basePath);

    if (requiresAllMandatoryLanguages(basePath)) {
      const buildMandatoryLanguageFields = (message) =>
        Yup.object().shape(
          mandatoryLanguages.reduce((shape, language) => {
            shape[language] = Yup.string().trim().required(message);
            return shape;
          }, {})
        );

      return Yup.object({
        description: buildMandatoryLanguageFields("Description is mandatory"),
        isfromtypeform: Yup.boolean().nullable(),
        prompttext: buildMandatoryLanguageFields(`${entityLabel} is mandatory`),
        tagids:
          basePath === "knowledge-hub" ||
          basePath === "allitude" ||
          basePath === "aiskilling"
            ? Yup.array().of(Yup.string())
            : Yup.array()
                .of(Yup.string())
                .required("At least one tag is required")
                .min(1, "At least one tag is required"),
        title: buildMandatoryLanguageFields("Title is mandatory"),
        tool_id: Yup.string(),
      });
    }

    return Yup.object({
      description: Yup.object(),
      isfromtypeform: Yup.boolean().nullable(),
      prompttext: Yup.object(),
      tagids:
        basePath === "knowledge-hub" ||
        basePath === "allitude" ||
        basePath === "aiskilling"
          ? Yup.array().of(Yup.string())
          : Yup.array()
              .of(Yup.string())
              .required("At least one tag is required")
              .min(1, "At least one tag is required"),
      title: Yup.object(),
      tool_id: isCyberTipsSection(basePath)
        ? Yup.string()
        : Yup.string().required("Tool is required"),
    }).test(
      "at-least-one-language-complete",
      `At least one language must have title, description and ${entityLabel.toLowerCase()} filled`,
      (value) =>
        editableLanguages.some((language) =>
          isPromptLanguageCompleteForSave(value, language)
        )
    );
  };

  const formatMultilingualField = (value) => {
    const normalized = ensurePromptMultilingualObject(value);
    if (typeof value === "string") {
      normalized.it = value;
    }
    return normalized;
  };

  useEffect(() => {
    if (rowData) {
      //&& tags.length > 0) { // tags became optional for some cases
      const tagIds = rowData?.tags
        .map((tagObj) => tags.find((tag) => tag.tagname?.en === tagObj?.en)?.id)
        .filter((tagId) => tagId);

      const newEntity = {
        ...rowData,
        description: formatMultilingualField(rowData.description),
        id: location.state?.isNew ? undefined : id,
        isfromtypeform: rowData.isfromtypeform ?? false,
        isNew: location.state?.isNew === true,
        note: rowData.note || "",
        prompttext: formatMultilingualField(rowData.prompttext),
        processed: rowData.processed ?? false,
        tagids: tagIds,
        title: formatMultilingualField(rowData.title),
        tool_id: rowData.tool_id || ""
      };

      setConfigurationEntity(newEntity);
    }
  }, [id, location.state, rowData, tags]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: configurationEntity || {
      description: ensurePromptMultilingualObject(),
      isNew: true,
      isfromtypeform: false,
      note: "",
      prompttext: ensurePromptMultilingualObject(),
      processed: false,
      tagids: [],
      title: ensurePromptMultilingualObject(),
      tool_id: ""
    },
    onSubmit: async (values, { resetForm }) => {
      if (isUserPromptEdit) {
        const cleanedNote = values.note?.trim() ? values.note : null;
        const selectedTags = tags
          .filter((tag) => values.tagids.includes(tag.id))
          .map((tag) => tag.tagname);
        await updatePrompt(
          {
            id: values.id,
            isNew: false,
            note: cleanedNote,
            processed: values.processed,
            tagids: values.tagids,
            tags: selectedTags,
            toolid: values.tool_id,
            tool_id: values.tool_id,
            toolname:
              tools.find((tool) => tool.id === values.tool_id)?.toolname || ""
          },
          basePath
        );
        history.push(`/${basePath}/user-prompts`);
        return;
      }
      const selectedTags = tags
        .filter((tag) => values.tagids.includes(tag.id))
        .map((tag) => tag.tagname);

      const cleanedValues = {
        description: ensurePromptMultilingualObject(values.description),
        isfromtypeform: values.isfromtypeform,
        prompttext: ensurePromptMultilingualObject(values.prompttext),
        tagids: values.tagids,
        tags: selectedTags,
        title: ensurePromptMultilingualObject(values.title),
        ...(isCyberTipsSection(basePath)
          ? {}
          : {
              toolid: values.tool_id,
              toolname:
                tools.find((tool) => tool.id === values.tool_id)?.toolname || ""
            })
      };

      if (values.isNew) {
        cleanedValues.isNew = values.isNew;
      } else {
        cleanedValues.id = values.id;
      }

      await updatePrompt({ isNew: values.isNew, ...cleanedValues }, basePath);
      history.push(`/${basePath}/prompt`);
    },
    validationSchema: isUserPromptEdit
      ? undefined
      : getValidationSchema(basePath, editableLanguages)
  });

  const canSaveUserPrompt = useMemo(() => {
    if (!isUserPromptEdit) {
      return false;
    }
    const initialProcessed = Boolean(formik.initialValues.processed);
    const currentProcessed = Boolean(formik.values.processed);
    const initialNote = formik.initialValues.note || "";
    const currentNote = formik.values.note || "";
    const initialTagIds = [...(formik.initialValues.tagids || [])].sort();
    const currentTagIds = [...(formik.values.tagids || [])].sort();
    const tagsChanged =
      initialTagIds.length !== currentTagIds.length ||
      initialTagIds.some((tagId, index) => tagId !== currentTagIds[index]);
    const toolChanged =
      (formik.initialValues.tool_id || "") !== (formik.values.tool_id || "");
    return (
      initialProcessed !== currentProcessed ||
      initialNote !== currentNote ||
      tagsChanged ||
      toolChanged
    );
  }, [
    formik.initialValues.note,
    formik.initialValues.processed,
    formik.initialValues.tagids,
    formik.initialValues.tool_id,
    formik.values.note,
    formik.values.processed,
    formik.values.tagids,
    formik.values.tool_id,
    isUserPromptEdit
  ]);

  const handleCreatePromptFromUserPrompt = useCallback(() => {
    history.push("/copilot/prompt/new", {
      isNew: true,
      rowData: {
        description: formik.values.description,
        isfromtypeform: formik.values.isfromtypeform,
        prompttext: formik.values.prompttext,
        tags: formik.values.tags || rowData?.tags || [],
        title: formik.values.title,
        tool_id: formik.values.tool_id || ""
      }
    });
  }, [
    formik.values.description,
    formik.values.isfromtypeform,
    formik.values.prompttext,
    formik.values.tags,
    formik.values.title,
    formik.values.tool_id,
    history,
    rowData?.tags
  ]);

  if (isLoading) {
    return (
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        width="100%"
      >
        <Typography variant="subtitle1">Loading...</Typography>
      </Box>
    );
  }

  if (!formik.values) {
    return (
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        width="100%"
      >
        <Typography variant="subtitle1">
          {isCyberTipsSection(basePath)
            ? `${getCyberTipsEntityLabel()} is not found 😢`
            : "Prompt is not found 😢"}
        </Typography>
      </Box>
    );
  }

  return (
    <Prompt
      basePath={basePath}
      canSaveUserPrompt={canSaveUserPrompt}
      formik={formik}
      isUserPromptEdit={isUserPromptEdit}
      preferredLanguage={preferredLanguage}
      setPreferredLanguage={setPreferredLanguage}
      tags={tags}
      tools={tools}
      validLanguages={editableLanguages}
      onCreatePromptFromUserPrompt={handleCreatePromptFromUserPrompt}
    />
  );
};

export default PromptContainer;
